import { FC, ReactNode, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DownloadLink } from "pages/download/DownloadLink";
import {
  AccountContext,
  EDITOR_ROLES,
  LPI_ROLES,
  RoleType,
  VIEWER_ROLES,
} from "auth/accountProvider";

interface MenuItem {
  id: string;
  title: string;
  route: string;
  roles: RoleType[];
  linkComponent?: ReactNode;
  enabled?: boolean;
}

const menuItems: MenuItem[] = [
  {
    id: "overview",
    title: "overview",
    route: "/",
    roles: VIEWER_ROLES,
  },
  // LPI is a purchased module, not a permission level, so this gate is
  // LPI_ROLES and not VIEWER_ROLES. An editor without the lpi grant must not
  // see this entry.
  {
    id: "LPIreportinge",
    title: "LPIreporting",
    route: "/lpi-reports",
    roles: LPI_ROLES,
  },
  {
    id: "CPIreporting",
    title: "CPIreporting",
    route: "/cpi-reports",
    roles: VIEWER_ROLES,
  },
  {
    id: "data",
    title: "data",
    route: "/data",
    roles: EDITOR_ROLES,
  },
  // Hidden for now (2026-08-31). The page still exists and its route is still
  // editor-gated, so it is reachable by direct URL — this only takes it out of
  // the nav. It duplicates the upload flow in /data; flip `enabled` to restore.
  {
    id: "uploadAndTemplates",
    title: "uploadAndTemplates",
    route: "/upload",
    roles: EDITOR_ROLES,
    enabled: false,
  },
  // Hidden for now (2026-08-31) — not needed.
  {
    id: "downloadReport",
    title: "",
    route: "",
    roles: VIEWER_ROLES,
    linkComponent: <DownloadLink />,
    enabled: false,
  },
  // The admin area (/admin/media, /admin/deletions) is intentionally NOT listed
  // here — it is super_admin-gated and reachable by direct URL only, kept out
  // of the nav.
];

const MainMenu: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasRole } = useContext(AccountContext);

  return (
    <ul className="nav navbar-nav nav-tabs first-level" role="menubar">
      {menuItems
        .filter((item) => item.enabled !== false)
        .map((item) => {
        const className = location.pathname === item.route ? "active" : "";

        return hasRole(item.roles) ? (
          <li key={item.id} className={className}>
            {item.linkComponent ?? (
              <Link
                to={item.route}
                className="nav-item fl-menu-item"
                role="menuitem"
              >
                {t(item.title)}
              </Link>
            )}
          </li>
        ) : null;
      })}
    </ul>
  );
};

export default MainMenu;
